// Presentation and observation only: never controls SIP, timers or routing.
const CALL_FAILURE_MESSAGES: Record<number, string> = {
  400: "Не удалось выполнить вызов: некорректный запрос телефонии (400). Обратитесь к администратору",
  401: "Не удалось авторизовать вызов (401). Обратитесь к администратору",
  403: "Телефония отклонила вызов (403). Обратитесь к администратору",
  404: "Номер не найден (404). Проверьте номер набора",
  407: "Не удалось авторизовать вызов (407). Обратитесь к администратору",
  408: "Не удалось установить соединение (408). Попробуйте позвонить позже",
  410: "Номер больше недоступен (410). Уточните номер",
  480: "Абонент временно недоступен (480). Попробуйте позвонить позже",
  484: "Номер указан не полностью (484). Проверьте номер набора",
  486: "Абонент занят (486). Попробуйте позвонить позже",
  487: "Вызов отменён до соединения (487)",
  488: "Не удалось согласовать параметры звука (488). Обратитесь к администратору",
  500: "Не удалось выполнить вызов: ошибка на стороне телефонии (500). Повторите позже; если повторяется — сообщите администратору",
  502: "Не удалось выполнить вызов: ошибка соединения между серверами телефонии (502). Попробуйте позже",
  503: "Телефония временно не может выполнить вызов (503). Попробуйте позвонить позже",
  504: "Не удалось установить соединение: истекло время ожидания между серверами телефонии (504). Попробуйте позже",
  580: "Не удалось согласовать условия соединения (580). Обратитесь к администратору",
  600: "Абонент занят (600). Попробуйте позвонить позже",
  603: "Вызов отклонён (603). Попробуйте связаться позже",
  604: "Номер не найден (604). Проверьте номер набора",
  606: "Не удалось согласовать параметры звонка (606). Обратитесь к администратору"
};

export function outgoingFailureMessage(response: any): string {
  const statusCode = Number(response?.message?.statusCode || 0);
  // Asterisk can return 603 with normal clearing after an unanswered Dial.
  // This is not evidence of a deliberate rejection or of who ended the call.
  if (statusCode === 603 && sipFailureEvidence(response).q850ReasonCode === 16) {
    return "Звонок завершён без ответа. Попробуйте позвонить позже (603)";
  }
  if (CALL_FAILURE_MESSAGES[statusCode]) return CALL_FAILURE_MESSAGES[statusCode];
  const code = Number.isInteger(statusCode) && statusCode >= 300 && statusCode <= 699 ? ` (${statusCode})` : "";
  // An unrecognised reason phrase may contain internal addresses or caller data.
  return `Не удалось выполнить вызов${code}. Если ошибка повторяется, сообщите администратору`;
}

export function audioFailureMessage(error: any, operation: "output" | "playback"): string {
  if (operation === "output") {
    if (error?.name === "NotFoundError") return "Выбранные наушники или динамики недоступны. Выберите устройство вывода звука в настройках софтфона";
    if (error?.name === "NotAllowedError") return "Браузер не разрешил использовать выбранное устройство звука. Проверьте разрешения сайта и выберите устройство вывода в настройках софтфона";
    if (error?.name === "NotSupportedError") return "Браузер не поддерживает выбор устройства звука. Выберите «По умолчанию» в настройках софтфона";
    if (error?.name === "AbortError") return "Не удалось переключить устройство звука. Повторно выберите устройство вывода в настройках софтфона";
    return "Не удалось включить звук в выбранном устройстве. Проверьте подключение наушников или динамиков и выберите устройство вывода в настройках софтфона";
  }
  if (error?.name === "NotAllowedError") return "Браузер заблокировал воспроизведение звука. Разрешите звук для сайта и повторно выберите устройство вывода в настройках софтфона";
  return "Не удалось воспроизвести звук разговора. Проверьте устройство вывода в настройках софтфона";
}

export function sipFailureEvidence(response: any): Record<string, string | number> {
  const evidence: Record<string, string | number> = { failureSource: "sip_response" };
  // These are codes received by the browser from Asterisk, NOT necessarily the
  // provider's original wire response. No free text, headers or SDP is retained.
  try {
    const message = response?.message;
    const headers = message?.getHeaders?.("Reason") ?? [message?.getHeader?.("Reason")];
    if (!Array.isArray(headers) || headers.length > 4) return evidence;
    const values = new Map<string, Set<number>>();
    for (const header of headers) {
      if (typeof header !== "string" || header.length > 1024 || /[\r\n]/.test(header)) continue;
      const numeric = header.replace(/"(?:[^"\\]|\\.)*"/g, '""');
      if (numeric.replace(/""/g, "").includes('"')) continue;
      for (const value of numeric.split(",")) {
        const parts = value.split(";");
        const protocol = parts.shift()?.trim().toUpperCase();
        const key = protocol === "Q.850" ? "q850ReasonCode" : protocol === "SIP" ? "sipReasonCode" : "";
        if (!key) continue;
        const codes = values.get(key) || new Set<number>();
        values.set(key, codes);
        for (const part of parts) {
          const match = /^\s*cause\s*=\s*(\d{1,3})\s*$/i.exec(part);
          if (!match) continue;
          const code = Number(match[1]);
          if (protocol === "Q.850" ? code <= 127 : code >= 100 && code <= 699) codes.add(code);
        }
      }
    }
    for (const [key, codes] of values) if (codes.size === 1) evidence[key] = [...codes][0];
  } catch { /* Observation must never prevent termination processing. */ }
  return evidence;
}
